import { CircleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { Integration, IntegrationStatus } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getChannelKey } from '@/util/channelName';
import { timeFromNowCompact } from '@/util/dateUtils';

export const ActiveFieldset = ({ integration }: { integration: Integration }) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const { updateIntegration, updateStatus } = useIntegrations(
    integration?.desk_id,
    integration?.integration_id
  );

  const isDisabled = !canWrite || updateStatus === 'pending';
  const isVerifiedButInactive =
    integration.status === IntegrationStatus.VERIFIED && !integration.active;

  const handleToggle = (checked: boolean) => {
    updateIntegration({ ...integration, active: checked });
  };

  const getTooltipMessage = () => {
    if (!canWrite) {
      return t('common.no_permission');
    }
    return t('integrations.active_card.description');
  };

  return (
    <Card className="rounded-md border-border py-0 shadow-none">
      <CardContent className="flex flex-col gap-3 px-4 pt-4 pb-5">
        <div className="flex items-center gap-4">
          <div className="flex flex-1 flex-col gap-1">
            <p className="text-sm leading-none font-medium">{t(getChannelKey(integration.type))}</p>
            {integration.last_session_at && (
              <p className="text-sm leading-5 text-muted-foreground">
                {t('integrations.last_conversation')}:{' '}
                {timeFromNowCompact(integration.last_session_at, { withSuffix: true })}
              </p>
            )}
          </div>
          <InfoTooltip content={getTooltipMessage()} size="large">
            <div>
              <Switch
                checked={integration.active || false}
                onCheckedChange={handleToggle}
                disabled={isDisabled}
                className="data-[state=checked]:bg-selected"
              />
            </div>
          </InfoTooltip>
        </div>
        {isVerifiedButInactive && (
          <div className="flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-warning">
            <CircleAlertIcon className="h-4 w-4 shrink-0" />
            <p className="text-sm">{t('integrations.inactive_warning')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
